import type { SceneDocument } from "./types";

const fullCanvas = { x: 0, y: 0, width: 2400, height: 1600 } as const;

export const scene = {
  schemaVersion: 1,
  id: "i-before-language",
  title: "i — A life before language",
  description: "One enclosed room, one repeated meal, and a person beginning before she has language for herself.",
  artworkStatus: "production",
  artboard: { width: 2400, height: 1600 },
  poster: {
    src: "/artwork/i/poster.jpg",
    alt: "A quiet adolescent girl sits beside a low table and bowl in warm window light while her father pauses in a shadowed doorway with a tray.",
  },
  initialCamera: {
    desktop: { center: { x: 1200, y: 800 }, zoom: 1 },
    mobile: { center: { x: 1200, y: 800 }, zoom: 1 },
  },
  layers: [
    {
      id: "environment",
      title: "The room",
      description: "A complete repainted environment plate with the hidden room restored.",
      src: "/artwork/i/environment.png",
      srcLow: "/artwork/i/environment.low.png",
      bounds: fullCanvas,
      z: 10,
      depth: 0.04,
      motionGroup: "room",
      opacity: 1,
      visible: true,
      blendMode: "normal",
      required: true,
      revealAt: 0,
    },
    {
      id: "father",
      title: "The doorway",
      description: "The father, tray, and immediate contact shading kept as one motion group.",
      src: "/artwork/i/father.png",
      srcLow: "/artwork/i/father.low.png",
      bounds: fullCanvas,
      z: 20,
      depth: 0.13,
      motionGroup: "doorway",
      opacity: 1,
      visible: true,
      blendMode: "normal",
      required: true,
      revealAt: 0.24,
    },
    {
      id: "subject",
      title: "The girl and bowl",
      description: "The girl, bowl, and their associated contact shading kept together.",
      src: "/artwork/i/subject.png",
      srcLow: "/artwork/i/subject.low.png",
      bounds: fullCanvas,
      z: 30,
      depth: 0.3,
      motionGroup: "subject",
      opacity: 1,
      visible: true,
      blendMode: "normal",
      required: true,
      revealAt: 0.48,
    },
    {
      id: "foreground",
      title: "The low table",
      description: "The nearest table and its contact marks preserve foreground depth.",
      src: "/artwork/i/foreground.png",
      srcLow: "/artwork/i/foreground.low.png",
      bounds: fullCanvas,
      z: 40,
      depth: 0.52,
      motionGroup: "foreground",
      opacity: 1,
      visible: true,
      blendMode: "normal",
      required: true,
      revealAt: 0.72,
    },
  ],
  hotspots: [
    {
      id: "bowl",
      label: "Read about the meal",
      layerId: "subject",
      point: { x: 995, y: 1000 },
      chapterId: "the-meal",
    },
    {
      id: "window",
      label: "Read about the room",
      layerId: "environment",
      point: { x: 1870, y: 175 },
      chapterId: "the-room",
    },
  ],
  chapters: [
    {
      id: "the-room",
      title: "The room",
      paragraphs: [
        "She knows the pale square of morning on the floor, the bed against the wall, and the doorway that opens only when he returns.",
        "There may be a world outside it. No one has given her the language to ask.",
      ],
      camera: {
        desktop: { center: { x: 1300, y: 760 }, zoom: 1.08 },
        mobile: { center: { x: 1270, y: 760 }, zoom: 1.04 },
      },
      emphasis: { environment: 1, father: 0.86, subject: 0.95, foreground: 0.92 },
    },
    {
      id: "the-meal",
      title: "The meal",
      paragraphs: [
        "A bowl appears at the same hour. It proves that time has passed and that he is still there.",
        "Everything necessary is provided. Nothing is explained. Routine becomes the only grammar she can read.",
      ],
      camera: {
        desktop: { center: { x: 1280, y: 930 }, zoom: 1.17 },
        mobile: { center: { x: 1230, y: 890 }, zoom: 1.08 },
      },
      emphasis: { environment: 0.92, father: 0.74, subject: 1, foreground: 1 },
    },
    {
      id: "before-i",
      title: "Before I",
      paragraphs: [
        "Light changes. Water dries. A hand hesitates before reaching for the bowl. Small differences gather where words should be.",
        "Somewhere inside that attention, a person begins—before she can name herself.",
      ],
      camera: {
        desktop: { center: { x: 1540, y: 710 }, zoom: 1.14 },
        mobile: { center: { x: 1460, y: 735 }, zoom: 1.08 },
      },
      emphasis: { environment: 0.9, father: 0.66, subject: 1, foreground: 0.9 },
    },
  ],
} as const satisfies SceneDocument;

const freeze = <T>(value: T): Readonly<T> => {
  if (value && typeof value === "object") {
    Object.freeze(value);
    Object.values(value).forEach((entry) => freeze(entry));
  }
  return value;
};

freeze(scene);
